import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { authVerifyEmail, authResendVerification } from "@/lib/api";
import AuthShell from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, MailCheck, MailX } from "lucide-react";
import { toast } from "sonner";

// Module-level guard: ensures a verification token is processed exactly once,
// even under React 18 StrictMode double-mounting (the token is single-use).
const processedTokens = new Set();

export default function EmailVerify() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const token = params.get("token");
  const [status, setStatus] = useState("verifying"); // verifying | error
  const [errorMsg, setErrorMsg] = useState("");
  const [email, setEmail] = useState("");
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setErrorMsg("Kein Bestätigungslink gefunden.");
      return;
    }
    if (processedTokens.has(token)) return;
    processedTokens.add(token);
    (async () => {
      try {
        const data = await authVerifyEmail(token);
        setUser(data.user);
        toast.success("E-Mail bestätigt. Willkommen bei BeSca!");
        navigate("/", { replace: true });
      } catch (err) {
        setStatus("error");
        const d = err?.response?.data?.detail;
        setErrorMsg(typeof d === "string" ? d : "Der Bestätigungslink ist ungültig oder abgelaufen.");
      }
    })();
  }, [token, setUser, navigate]);

  const resend = async () => {
    if (!email.trim()) {
      toast.error("Bitte geben Sie Ihre E-Mail-Adresse ein.");
      return;
    }
    setResending(true);
    try {
      await authResendVerification(email.trim());
      toast.success("Falls ein unbestätigtes Konto existiert, wurde eine neue E-Mail gesendet.");
    } catch {
      toast.error("Senden fehlgeschlagen. Bitte später erneut versuchen.");
    } finally {
      setResending(false);
    }
  };

  if (status === "verifying") {
    return (
      <AuthShell title="E-Mail wird bestätigt" subtitle="Einen Moment bitte …">
        <div className="flex flex-col items-center gap-3 py-6" data-testid="verify-loading">
          <Loader2 className="h-10 w-10 animate-spin text-primary" />
          <MailCheck className="h-6 w-6 text-muted-foreground" />
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Bestätigung fehlgeschlagen"
      subtitle="Der Link konnte nicht bestätigt werden."
      footer={
        <span>
          <Link to="/login" className="text-primary font-medium hover:underline" data-testid="link-login">
            Zur Anmeldung
          </Link>
        </span>
      }
    >
      <div className="space-y-4 text-center" data-testid="verify-error">
        <MailX className="h-12 w-12 text-destructive mx-auto" />
        <p className="text-sm text-destructive">{errorMsg}</p>
        <p className="text-sm text-muted-foreground">
          Fordern Sie einen neuen Bestätigungslink an:
        </p>
        <Input
          type="email"
          placeholder="Ihre E-Mail-Adresse"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="verify-resend-email"
        />
        <Button
          className="w-full"
          onClick={resend}
          disabled={resending}
          data-testid="verify-resend-button"
        >
          {resending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Neue Bestätigung anfordern"}
        </Button>
      </div>
    </AuthShell>
  );
}
